import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { register } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import '../styles/Signin.css';

function Signup() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    hasDigit: false,
    hasSpecial: false
  });
  
  const { setError: setAuthError, currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get redirect URL from query params or from referrer
  const params = new URLSearchParams(location.search);
  const rawRedirectUrl = params.get('redirect') || document.referrer || '/';
  
  // Clean the redirect URL to prevent redirection loops and auth page redirects
  const redirectUrl = React.useMemo(() => {
    // Parse the URL to see if it's an internal URL
    let cleanUrl = rawRedirectUrl;
    let fragment = '';
    
    try {
      // If it's a full URL, extract just the path and fragment
      if (rawRedirectUrl.startsWith('http')) {
        const urlObj = new URL(rawRedirectUrl);
        cleanUrl = urlObj.pathname + urlObj.search;
        fragment = urlObj.hash;
      } else {
        // Handle local paths with fragments
        const hashIndex = cleanUrl.indexOf('#');
        if (hashIndex !== -1) {
          fragment = cleanUrl.substring(hashIndex);
          cleanUrl = cleanUrl.substring(0, hashIndex);
        }
      }
      
      // Don't redirect back to auth pages
      const authPages = ['/signin', '/signup', '/forgot-password', '/reset-password', '/verify-email'];
      for (const authPage of authPages) {
        if (cleanUrl.startsWith(authPage)) {
          return '/'; // Redirect to home instead
        }
      }
      
      // Return the cleaned path with the fragment if it exists
      return cleanUrl + fragment;
    } catch (e) {
      console.error('Error parsing redirect URL:', e);
      return '/'; // Default to home on error
    }
  }, [rawRedirectUrl]);

  // Redirect if already signed in
  useEffect(() => {
    if (currentUser) {
      navigate(redirectUrl !== '/signup' ? decodeURIComponent(redirectUrl) : '/profile');
    }
  }, [currentUser, navigate, redirectUrl]);

  const validatePassword = (password) => {
    const hasDigit = /\d/.test(password);
    const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
    const isLongEnough = password.length >= 8;

    setPasswordValidation({
      length: isLongEnough,
      hasDigit,
      hasSpecial
    });

    return isLongEnough && hasDigit && hasSpecial;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'password') {
      validatePassword(value);
    }
  };

  const validateForm = () => {
    if (!formData.email || !formData.password) {
      setError('Email and password are required');
      return false;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    
    if (!validatePassword(formData.password)) {
      setError('Password does not meet all requirements');
      return false;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setError('');
      setLoading(true);
      
      // Format data for API
      const userData = {
        email: formData.email,
        password: formData.password,
        first_name: formData.firstName,
        last_name: formData.lastName
      };
      
      await register(userData);
      
      // Show success message
      setSuccess(true);
      
      // Redirect to verification page with email
      setTimeout(() => {
        navigate(`/verify-email?email=${encodeURIComponent(formData.email)}&redirect=${encodeURIComponent(redirectUrl)}`);
      }, 2000);
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h2>Create Account</h2>
        
        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">
          Account created successfully! Redirecting to verification page...
        </div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <input
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Enter your first name"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Enter your last name"
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Email*</label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              required
            />
            <small className="form-text">
              Your email will be used as your username
            </small>
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password*</label>
            <input
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Create a password"
              required
            />
            <div className="password-requirements">
              <div className={`requirement-item ${passwordValidation.length ? 'valid' : ''}`}>
                <span className="check-icon">✓</span>
                At least 8 characters
              </div>
              <div className={`requirement-item ${passwordValidation.hasDigit ? 'valid' : ''}`}>
                <span className="check-icon">✓</span>
                Contains a number
              </div>
              <div className={`requirement-item ${passwordValidation.hasSpecial ? 'valid' : ''}`}>
                <span className="check-icon">✓</span>
                Contains a special character
              </div>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password*</label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm your password"
              required
            />
            {formData.confirmPassword && (
              <div className={`password-match ${formData.password === formData.confirmPassword ? 'valid' : ''}`}>
                <span className="check-icon">✓</span>
                Passwords match
              </div>
            )}
          </div>
          
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>
        
        <div className="auth-links">
          <Link to={`/signin${location.search}`}>Already have an account? Sign in</Link>
        </div>
      </div>
    </div>
  );
}

export default Signup;